import { NotImplementedError } from "../Errors";

import {
    ConvertedPDFMetaResponse,
    ConvertedPdfResponse,
    DraftSettings,
    FufWithAttachmentsResponse,
    GetUserResponse,
    ICandyApiClient,
    ICandyApiDraftClient,
    IInnerChangeResult,
    IInnerChangeSuccessResult,
    NormalizationProgressTask,
    FufWithAttachmentsTaskResult,
    FufWithScenarioTaskResult,
    GetPartialFufResponse,
} from "./ICandyApiClient";
import type { ServerErrorLog } from "./Types/ErrorLog/ServerErrorLog";
import type { ServerInnerResponse, ServerJsonResponse } from "./Types/GetInner/ServerInnerResponse";
import {
    ExcelsToPartialFufsResponse,
    FileProperties,
    ServerErrorResponse,
    UploadFilePropertiesResponse,
} from "./Types/DraftActions/FileProperties";
import { CreateDraftResponse } from "./Types/CreateDraft/CreateDraftResponse";
import { CreateExplanatoryNoteResponse } from "./Types/ExplanatoryNote/CreateExplanatoryNoteResponse";
import { ServerTask } from "./Types/Tasks/Tasks";
import { GetByInnResponse } from "./Types/Focus/FocusTypes";
import { AttachInfo } from "./Types/Attachments/Attacments";
import { ServerRequisitesResponse } from "./Types/GetInner/ServerRequisitesResponse";
import { CombineFufResult } from "./Types/DraftActions/CombineFufResult";
import { FeatureFlagsResponse } from "./Types/FeatureFlags/FeatureFlagsResponse";
import { ErrorCode } from "./CandyApiErrors";
import { PrintResponse } from "./Types/Print/PrintResponse";
import { ServerRawResponse } from "./Types/GetInner/ServerRawResponse";
import { CandyPopdProtocol } from "./Types/ErrorLog/CandyPopdProtocol";
import { InnerQueryResponse } from "./Types/QueryCollecton/InnerQueryResponse";
import { ResolvePathResponse } from "./Types/ErrorLog/ResolvePathResponse";
import { InnerHistoryResponse, InnerHistoryUndoResponseWithStatus } from "./Types/HistoryEditing/InnerHistoryResponse";
import { PollingTaskWithResult } from "./Types/PollingTasks/PollingTasks";

export class FakeCandyApiClient implements ICandyApiClient {
    public getAllGfvs(): Promise<string[]> {
        return Promise.resolve([]);
    }

    public forceGetAllGfvs(): Promise<string[]> {
        return Promise.resolve([]);
    }

    public bindExternOrganization(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public getWorkspaceDirectoryId(): Promise<string> {
        return Promise.resolve("");
    }

    public checkAlive(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public createDraft(ns: string, gfv: string): Promise<CreateDraftResponse>;
    public createDraft(ns: string, gfv: undefined, body: Buffer): Promise<CreateDraftResponse>;
    public createDraft(): Promise<CreateDraftResponse> {
        throw new NotImplementedError();
    }

    public forDraft(): ICandyApiDraftClient {
        return new FakeCandyApiDraftClient();
    }

    public createDraftFromDrive(): Promise<CreateDraftResponse> {
        throw new NotImplementedError();
    }

    public createEmptyDraft(): Promise<CreateDraftResponse> {
        throw new NotImplementedError();
    }

    public getInfoByInns(): Promise<GetByInnResponse> {
        throw new NotImplementedError();
    }

    public getFeatureFlagsWithoutPromise(featureFlags: FeatureFlagsResponse): FeatureFlagsResponse {
        return featureFlags;
    }

    public getFeatureFlags(): Promise<FeatureFlagsResponse> {
        const response: FeatureFlagsResponse = { candyTestFeature: "true" };
        return Promise.resolve(response);
    }

    public convertToPdfAndGetMeta(_driveUri: string): Promise<ConvertedPDFMetaResponse> {
        throw new NotImplementedError();
    }

    public convertToPdf(_driveUri: string): Promise<ConvertedPdfResponse> {
        throw new NotImplementedError();
    }
}

export class FakeCandyApiDraftClient implements ICandyApiDraftClient {
    public deleteInstances(): Promise<void> {
        return Promise.resolve();
    }

    public getInnerFuf(): Promise<string | undefined> {
        return Promise.resolve(undefined);
    }

    public async startGetInnerFufTask(): Promise<PollingTaskWithResult<string>> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }

    public async getInnerFufTask(): Promise<PollingTaskWithResult<string>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: undefined,
        });
    }

    public getFufWithAttachments(): Promise<FufWithAttachmentsResponse | undefined> {
        return Promise.resolve(undefined);
    }

    public async startGetFufWithAttachmentsTask(): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }

    public async getFufWithAttachmentsTask(): Promise<PollingTaskWithResult<FufWithAttachmentsTaskResult>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: undefined,
        });
    }

    public async startTransferTask(): Promise<PollingTaskWithResult<CombineFufResult>> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }

    public async getTransferTask(): Promise<PollingTaskWithResult<CombineFufResult>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: undefined,
        });
    }

    public async startGetFufWithScenarioTask(): Promise<PollingTaskWithResult<FufWithScenarioTaskResult>> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }

    public async getFufWithScenarioTask(): Promise<PollingTaskWithResult<FufWithScenarioTaskResult>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: undefined,
        });
    }

    public importEmployees(): Promise<void> {
        return Promise.resolve();
    }

    public getPartialFuf(): Promise<GetPartialFufResponse | undefined> {
        return Promise.resolve(undefined);
    }

    public runMassAction(): Promise<void> {
        return Promise.resolve();
    }

    public acceptMultipleTransferType(): Promise<ServerErrorResponse | undefined> {
        return Promise.resolve(undefined);
    }

    public cancelTask(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public clearDraft(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public restoreToSourceFuf(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public convertDraftFromPrevGfv(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public createOrUpdateAttachments(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public enqueueTask(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public getAllAttachments(): Promise<AttachInfo[]> {
        return Promise.resolve([]);
    }

    public getAllTasks(): Promise<ServerTask[]> {
        return Promise.resolve([]);
    }

    public getAttachmentById(): Promise<AttachInfo> {
        throw new NotImplementedError();
    }

    public createDraftAttachment(): Promise<AttachInfo> {
        throw new NotImplementedError();
    }

    public openLoadedAttachmentAsDraft(): Promise<AttachInfo> {
        throw new NotImplementedError();
    }

    public getErrorLog(): Promise<ServerErrorLog> {
        throw new NotImplementedError();
    }

    public async getErrorLogWithCheckScenarios(): Promise<ServerErrorLog> {
        throw new NotImplementedError();
    }

    public getExpertErrorLog(): Promise<ServerErrorLog> {
        throw new NotImplementedError();
    }

    public getExplanatoryNote(): Promise<CreateExplanatoryNoteResponse> {
        throw new NotImplementedError();
    }

    public print(): Promise<PrintResponse> {
        throw new NotImplementedError();
    }

    public getFileProperties(): Promise<FileProperties | undefined> {
        return Promise.resolve(undefined);
    }

    public prepareAndGetFileProperties(): Promise<FileProperties | undefined> {
        return Promise.resolve(undefined);
    }

    public uploadAndGetFileProperties(): Promise<UploadFilePropertiesResponse> {
        return Promise.resolve({
            serverError: ErrorCode.UnknownError,
            sourcesTotalCount: 0,
            invalidSourceDriveContentNames: "",
        });
    }

    public async downloadFuf(): Promise<void> {
        // noop
    }

    public async downloadInner(): Promise<void> {
        // noop
    }

    public getHrefToDownloadInner(): string {
        return "";
    }

    public getHrefToConvertationLog(): string {
        return "";
    }

    public getHrefToDownloadClientInfo(): string {
        return "";
    }

    public getHrefToDownloadFormsClientInfo(): string {
        return "";
    }

    public getHrefToDownloadInnerFuf(): string {
        return "";
    }

    public getHrefToErrorLogPdf(): string {
        return "";
    }

    public getHrefToInnerEditor(): string {
        return "";
    }

    public async getRequisites(): Promise<ServerRequisitesResponse> {
        return {};
    }

    public async getInner(): Promise<ServerInnerResponse> {
        return {};
    }

    public getPartialInner(): Promise<ServerInnerResponse> {
        throw new NotImplementedError();
    }

    public getPartialInnerAsJsonTree(): Promise<ServerJsonResponse> {
        throw new NotImplementedError();
    }

    public getRawPartialInnerAsJsonTree(): Promise<ServerRawResponse> {
        throw new NotImplementedError();
    }

    public getTaskById(): Promise<ServerTask | undefined> {
        return Promise.resolve(undefined);
    }

    public normalizeByRules(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public patchPresentation(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public removeAttachments(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public async sendChangeSet(): Promise<IInnerChangeResult> {
        return Promise.resolve({ dataVersion: 0 });
    }

    public async sendChangeWithNormalizationSet(): Promise<IInnerChangeResult> {
        return Promise.resolve({ dataVersion: 0 });
    }

    public async startPatchWithNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeSuccessResult> | number> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }
    public async getPatchWithNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeSuccessResult>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: { dataVersion: 0 },
        });
    }

    public sendChangeSetSync(): IInnerChangeResult {
        return 200;
    }

    public setFufByDriveUri(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public importXmls(): Promise<CombineFufResult | undefined> {
        return Promise.resolve(undefined);
    }

    public setPresentation(): Promise<void> {
        return Promise.resolve(undefined);
    }

    public setMultipleWithNormalization(): Promise<IInnerChangeResult> {
        return Promise.resolve(200);
    }

    public normalizeDirtyMasks(): Promise<IInnerChangeResult> {
        return Promise.resolve(200);
    }

    public searchInstancesByText(): Promise<string[]> {
        return Promise.resolve([]);
    }

    public customNormalize(): Promise<IInnerChangeResult> {
        return Promise.resolve(200);
    }

    public async startCustomNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeResult>> {
        return Promise.resolve({
            id: "",
            status: "InProgress",
            progress: 0,
            errorInfo: undefined,
        });
    }

    public async getCustomNormalizationTask(): Promise<PollingTaskWithResult<IInnerChangeResult>> {
        return Promise.resolve({
            id: "",
            status: "Success",
            progress: 100,
            errorInfo: undefined,
            result: { dataVersion: 0 },
        });
    }

    public queryCollection(): Promise<InnerQueryResponse> {
        return Promise.resolve({ data: [], columns: [] });
    }

    public getDataVersion(): Promise<number> {
        return Promise.resolve(0);
    }

    public printExcel(): Promise<PrintResponse> {
        throw new NotImplementedError();
    }

    public resolveInnerPath(): Promise<ResolvePathResponse> {
        throw new NotImplementedError();
    }

    public excelsToPartialFufs(): Promise<ExcelsToPartialFufsResponse | undefined> {
        throw new NotImplementedError();
    }

    public checkByPopd(): Promise<CandyPopdProtocol> {
        throw new NotImplementedError();
    }

    public getDraftSettings(): Promise<DraftSettings> {
        throw new NotImplementedError();
    }

    public setDraftSettings(): Promise<void> {
        return Promise.resolve();
    }

    public getHistory(): Promise<InnerHistoryResponse | number> {
        return Promise.resolve([]);
    }

    public async undoToChange(): Promise<InnerHistoryUndoResponseWithStatus> {
        return Promise.resolve({ status: 200 });
    }
    public async getGFV(): Promise<string> {
        return Promise.resolve("");
    }
    public async getUser(): Promise<GetUserResponse> {
        return Promise.resolve({
            Id: null,
            Ip: null,
            FirstName: null,
            MiddleName: null,
            LastName: null,
        });
    }

    public async getFileNdsProperties(): Promise<FileProperties[] | undefined> {
        return Promise.resolve(undefined);
    }

    public makeDownloadDriveLink(): void {
        throw new NotImplementedError();
    }

    public getNormalizationTask(): Promise<NormalizationProgressTask> {
        throw new NotImplementedError();
    }

    public startNormalizationTask(): Promise<NormalizationProgressTask> {
        throw new NotImplementedError();
    }
}
